import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { LensTopicsExternal } from "../../api/Blueprints";
import {
  useCreateLensTopics,
  useGenerateLensTopics,
  useUpdateLensTopics,
} from "../../api/BlueprintsApi";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../shared-components/Button";
import InputField from "../../shared-components/InputField";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { Modal } from "../../shared-components/Modal";
import { classNames } from "../../utils";
import {
  isSupportedCsvContent,
  isSupportedCsvFile,
  mergeUniqueSeeds,
  readFileAsText,
  needsTopicRegenerate,
  normalizeSeeds,
  normalizeTopics,
  parseCsvSeeds,
  parseTypedSeeds,
  topicsFromModelResponse,
} from "./helpers";

type Props = {
  showModal: boolean;
  close: () => void;
  behaviorKitUUID: string;
  onSave: () => void;
  initial?: LensTopicsExternal;
};

const REGENERATE_REQUIRED_MESSAGE =
  "You have not regenerated topics after changing topic seeds.";
const GENERATE_TOPICS_REQUIRED_MESSAGE = "Please generate at least one topic";
const UNSUPPORTED_FILE_MESSAGE = "Unsupported file type or format";
const CSV_UPLOAD_ACCEPT = ".csv,text/csv";
const GROUP_NAME_MAX_LENGTH = 255;

function shouldIgnoreSeedBlur(relatedTarget: EventTarget | null): boolean {
  return (
    relatedTarget instanceof Element &&
    relatedTarget.closest("[data-seed-blur-ignore]") !== null
  );
}

export const LensTopicsButtonAdd = ({
  behaviorKitUUID,
  onSave,
}: {
  behaviorKitUUID: string;
  onSave: () => void;
}) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div>
      <Button
        text="Add Topics"
        colorType={ButtonColorTypes.custom}
        type={ButtonTypes.add}
        className="border p-0"
        onClick={(e) => {
          setShowModal(true);
          e.preventDefault();
        }}
      />
      <LensTopicsModal
        showModal={showModal}
        close={() => setShowModal(false)}
        behaviorKitUUID={behaviorKitUUID}
        onSave={onSave}
      />
    </div>
  );
};

export const LensTopicsModal = ({
  showModal,
  close,
  behaviorKitUUID,
  onSave,
  initial,
}: Props) => {
  const [name, setName] = useState((initial?.name ?? "").trim());
  const [seeds, setSeeds] = useState<string[]>(
    normalizeSeeds(initial?.seeds ?? [])
  );
  const [topics, setTopics] = useState<string[]>(
    normalizeTopics(initial?.topics ?? [])
  );
  const [seedInput, setSeedInput] = useState("");
  const [error, setError] = useState("");
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [generatedFromSeeds, setGeneratedFromSeeds] = useState<string[] | null>(
    initial?.topics?.length ? normalizeSeeds(initial.seeds ?? []) : null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const generateRequestIdRef = useRef(0);
  const isEdit = !!initial;

  const {
    mutate: createLensTopics,
    isLoading,
    isError,
    isSuccess,
    reset,
  } = useCreateLensTopics();
  const {
    mutate: updateLensTopics,
    isLoading: isLoadingUpdate,
    isError: isErrorUpdate,
    isSuccess: isSuccessUpdate,
    reset: resetUpdate,
  } = useUpdateLensTopics();
  const {
    mutate: generateLensTopics,
    isLoading: isGenerating,
    reset: resetGenerate,
  } = useGenerateLensTopics();

  const handleClose = useCallback(() => {
    generateRequestIdRef.current += 1;
    resetGenerate();
    close();
  }, [close, resetGenerate]);

  useEffect(() => {
    if (showModal) {
      setName((initial?.name ?? "").trim());
      setSeeds(normalizeSeeds(initial?.seeds ?? []));
      setTopics(normalizeTopics(initial?.topics ?? []));
      setGeneratedFromSeeds(
        initial?.topics?.length ? normalizeSeeds(initial.seeds ?? []) : null
      );
      setSeedInput("");
      setError("");
      setShowClearConfirm(false);
    }
  }, [showModal, initial]);

  useEffect(() => {
    if (isSuccess || isSuccessUpdate) {
      onSave();
      handleClose();
    }
    if (isError || isErrorUpdate) {
      setError("Error saving topics. Please try again.");
    }
    if (isError || isSuccess) {
      reset();
    }
    if (isErrorUpdate || isSuccessUpdate) {
      resetUpdate();
    }
  }, [
    isSuccessUpdate,
    isErrorUpdate,
    isSuccess,
    onSave,
    handleClose,
    reset,
    resetUpdate,
    isError,
  ]);

  function addSeedsFromText(text: string): string[] {
    const incoming = parseTypedSeeds(text);
    if (incoming.length === 0) {
      if (!text.trim()) {
        setSeedInput("");
      }
      return seeds;
    }
    const merged = mergeUniqueSeeds(seeds, incoming);
    setSeeds(merged);
    setSeedInput("");
    return merged;
  }

  async function handleCsvUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    if (!isSupportedCsvFile(file)) {
      setError(UNSUPPORTED_FILE_MESSAGE);
      return;
    }
    try {
      const text = await readFileAsText(file);
      if (!isSupportedCsvContent(text)) {
        setError(UNSUPPORTED_FILE_MESSAGE);
        return;
      }
      setError("");
      setSeeds(mergeUniqueSeeds(seeds, parseCsvSeeds(text)));
    } catch {
      setError(UNSUPPORTED_FILE_MESSAGE);
    }
  }

  function handleRegenerate() {
    const nextSeeds = addSeedsFromText(seedInput);
    if (nextSeeds.length === 0) {
      setError("Topic Seeds are required.");
      return;
    }
    setError("");
    const requestId = ++generateRequestIdRef.current;
    generateLensTopics(
      {
        id: initial?.external_id || behaviorKitUUID,
        keywords: nextSeeds,
      },
      {
        onSuccess: (data) => {
          if (requestId !== generateRequestIdRef.current) {
            return;
          }
          try {
            setTopics(topicsFromModelResponse(data));
            setGeneratedFromSeeds([...nextSeeds]);
            setError("");
          } catch {
            setError("Error generating topics. Please try again.");
          }
        },
        onError: () => {
          if (requestId !== generateRequestIdRef.current) {
            return;
          }
          setError("Error generating topics. Please try again.");
        },
      }
    );
  }

  function handleClearAll() {
    setSeeds([]);
    setTopics([]);
    setGeneratedFromSeeds(null);
    setShowClearConfirm(false);
  }

  function handleRemoveTopic(index: number) {
    setTopics((current) =>
      current.filter((_, itemIndex) => itemIndex !== index)
    );
  }

  function handleSave() {
    const nextName = name.trim();
    setName(nextName);
    const nextSeeds = addSeedsFromText(seedInput);
    if (!nextName) {
      setError("Group Name is required.");
      return;
    }
    if (nextName.length > GROUP_NAME_MAX_LENGTH) {
      setError(
        `Group Name must not exceed ${GROUP_NAME_MAX_LENGTH} characters.`
      );
      return;
    }
    if (nextSeeds.length === 0) {
      setError("Topic Seeds are required.");
      return;
    }
    if (topics.length === 0) {
      setError(GENERATE_TOPICS_REQUIRED_MESSAGE);
      return;
    }
    if (needsTopicRegenerate(nextSeeds, topics, generatedFromSeeds)) {
      setError(REGENERATE_REQUIRED_MESSAGE);
      return;
    }
    const payload = {
      behavior_kit_id: behaviorKitUUID,
      name: nextName,
      seeds: nextSeeds,
      topics: normalizeTopics(topics),
    };
    if (isEdit) {
      updateLensTopics({
        ...payload,
        external_id: initial?.external_id ?? "",
      });
    } else {
      createLensTopics(payload);
    }
  }

  const topicsMissing = error === GENERATE_TOPICS_REQUIRED_MESSAGE;

  return (
    <Modal open={showModal} onClose={handleClose} width="w-full max-w-2xl">
      <div className="relative">
        <div className="text-2xl font-bold text-gray-900 border-b border-gray-200 mb-5 pb-3">
          {isEdit ? "Edit Topics" : "Add Topics"}
        </div>
        <div className="pb-5">
          <InputField
            handleChange={(e) => {
              setName(e.target.value.slice(0, GROUP_NAME_MAX_LENGTH));
            }}
            handleBlur={({ target }) => {
              setName(target.value.trim().slice(0, GROUP_NAME_MAX_LENGTH));
            }}
            name="lens-topics-group-name"
            label="Group Name"
            required
            maxLength={GROUP_NAME_MAX_LENGTH}
            placeholder="Type here..."
            classes="w-full"
            className={classNames("mb-0 w-full")}
            value={name}
          />
        </div>
        <div className="pb-5">
          <div className="flex items-center text-sm font-medium text-gray-600 mb-2">
            <span>
              Topic Seeds <span className="text-red-400">*</span>
            </span>
            <button
              type="button"
              className="ml-3 text-blue-600 font-bold"
              onClick={() => fileInputRef.current?.click()}
            >
              Upload CSV
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={CSV_UPLOAD_ACCEPT}
              aria-label="Upload CSV file"
              className="hidden"
              onChange={handleCsvUpload}
            />
            <span className="ml-auto inline-flex items-center gap-2 relative">
              <span className="inline-flex items-center justify-center w-6 h-6 text-xs rounded-full bg-gray-200 text-gray-700">
                {seeds.length}
              </span>
              <button
                type="button"
                className="underline text-gray-600 hover:text-gray-800"
                onClick={() => {
                  if (seeds.length > 0 || topics.length > 0) {
                    setShowClearConfirm(true);
                  }
                }}
              >
                Clear All
              </button>
              {showClearConfirm && (
                <div
                  role="alertdialog"
                  aria-labelledby="clear-seeds-title"
                  aria-describedby="clear-seeds-description"
                  className="absolute top-full right-0 mt-2 z-20 bg-white rounded-lg shadow-xl px-6 py-5 w-80"
                >
                  <div
                    id="clear-seeds-title"
                    className="text-base font-bold text-gray-900"
                  >
                    Clear all Seeds?
                  </div>
                  <p
                    id="clear-seeds-description"
                    className="text-sm text-gray-500 mt-2 mb-6"
                  >
                    This removes all seeds and generated topics.
                  </p>
                  <div className="flex justify-end gap-3">
                    <Button
                      text="Cancel"
                      colorType={ButtonColorTypes.cancel}
                      onClick={() => setShowClearConfirm(false)}
                    />
                    <Button
                      text="OK"
                      colorType={ButtonColorTypes.secondary}
                      onClick={handleClearAll}
                    />
                  </div>
                </div>
              )}
            </span>
          </div>
          <div className="border border-gray-200 rounded-md p-3">
            <div className="flex border border-gray-200 rounded-md items-center overflow-hidden">
              <InputField
                handleChange={(e) => {
                  setSeedInput(e.target.value);
                }}
                handleEnter={() => addSeedsFromText(seedInput)}
                handleBlur={(event) => {
                  const relatedTarget = (
                    event as unknown as FocusEvent<HTMLInputElement>
                  ).relatedTarget;
                  if (shouldIgnoreSeedBlur(relatedTarget)) {
                    return;
                  }
                  addSeedsFromText(event.target.value);
                }}
                name="lens-topics-seed-input"
                placeholder="Type here.."
                classes="w-full"
                className={classNames("mb-0 w-full border-none")}
                value={seedInput}
              />
              <Button
                text="Add"
                onClick={(e) => {
                  e.preventDefault();
                  addSeedsFromText(seedInput);
                }}
                colorType={ButtonColorTypes.custom}
                type={ButtonTypes.add}
                className="border-l border-gray-200 p-0 w-20 bg-gray-50 rounded-l-none self-stretch"
              />
            </div>
            <div
              data-testid="topic-seeds-chips"
              className="border border-gray-200 rounded-md h-[7.5rem] p-2 mt-3 overflow-y-auto"
            >
              <div className="flex flex-wrap gap-2">
                {seeds.map((seed) => (
                  <span
                    key={seed}
                    className="inline-flex items-center text-sm rounded-full px-2.5 py-1 bg-green-100 text-gray-800 font-bold"
                  >
                    {seed}
                    <button
                      type="button"
                      className="ml-1.5 inline-flex items-center text-gray-500"
                      aria-label={`Remove ${seed}`}
                      onClick={() =>
                        setSeeds(seeds.filter((item) => item !== seed))
                      }
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div
              className="mt-3"
              data-seed-blur-ignore
              onMouseDown={(event) => event.preventDefault()}
            >
              <Button
                text="Regenerate Topics"
                colorType={ButtonColorTypes.outline}
                disabled={isGenerating}
                onClick={(e) => {
                  e.preventDefault();
                  handleRegenerate();
                }}
              />
            </div>
          </div>
        </div>
        <div className="pb-3">
          <div className="text-sm font-medium text-gray-600 mb-2">
            Topics Generated
          </div>
          <div
            data-testid="topics-generated"
            aria-invalid={topicsMissing || undefined}
            aria-describedby={
              topicsMissing ? "topics-generated-error" : undefined
            }
            className={classNames(
              "border rounded-md p-3 min-h-[8rem] max-h-44 overflow-y-auto bg-gray-50 text-gray-600",
              topicsMissing ? "border-red-500" : "border-gray-200"
            )}
          >
            {isGenerating ? (
              <div
                className="space-y-3"
                data-testid="topics-generated-skeleton"
                aria-busy="true"
                aria-label="Generating topics"
              >
                {["w-11/12", "w-full", "w-2/3"].map((width) => (
                  <div
                    key={width}
                    className={`${width} skeleton__grey skeleton-small rounded`}
                  />
                ))}
              </div>
            ) : topics.length === 0 ? (
              <p className="text-sm text-gray-400">
                Add words or phrase, then click Generate Topics to see topics.
              </p>
            ) : (
              <ul className="list-disc pl-5 text-sm space-y-1.5">
                {topics.map((topic, index) => (
                  <li
                    key={`${topic}-${index}`}
                    className="relative pr-6 break-words"
                  >
                    {topic}
                    <button
                      type="button"
                      className="absolute right-0 top-0 inline-flex items-center text-gray-500"
                      aria-label={`Remove ${topic}`}
                      data-seed-blur-ignore
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleRemoveTopic(index)}
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {error && (
          <p
            id={topicsMissing ? "topics-generated-error" : undefined}
            role="alert"
            className="pb-2 text-sm text-red-700"
          >
            {error}
          </p>
        )}
        <div
          className="flex justify-end gap-3 pt-5"
          data-seed-blur-ignore
          onMouseDown={(event) => event.preventDefault()}
        >
          {!isLoading && !isLoadingUpdate && (
            <Button
              text="Cancel"
              colorType={ButtonColorTypes.cancel}
              onClick={handleClose}
            />
          )}
          {(isLoading || isLoadingUpdate) && <LoadingSpinner />}
          {!isLoading && !isLoadingUpdate && !isGenerating && (
            <Button
              text="Save"
              onClick={(e) => {
                e.preventDefault();
                handleSave();
              }}
            />
          )}
        </div>
        {showClearConfirm && (
          <div
            className="absolute inset-0 z-10 bg-transparent"
            data-testid="clear-seeds-backdrop"
            onClick={() => setShowClearConfirm(false)}
          />
        )}
      </div>
    </Modal>
  );
};
